import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LanguageSelector } from "@/components/language-selector";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useInitialLanguage } from "@/hooks/use-initial-language";
import { env } from "@/lib/env";
import { useLoginForm } from "./use-login-form-enhanced";

// Prod-only one-tap sign-in to the shared demo salon. These credentials are
// intentionally public, so there is no secret to protect by routing through the
// backend. Gated by isProdDeploy so it exists ONLY on the production host.
const DEMO_ACCOUNT = { email: "demo@96beauty.de", password: "demo1234" } as const;

export function LoginPage() {
  const { t } = useTranslation("auth");
  const { t: tForms } = useTranslation("forms");
  const [showPassword, setShowPassword] = useState(false);

  // Handle initial language from URL query parameter
  useInitialLanguage();
  const { form, isLoading, loadingMessage, onSubmit, loginWith, loginWithDemo, demoSections } =
    useLoginForm();

  // Demo mode: Hide email/password form when demo users are available
  const isDemoMode = demoSections.length > 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      {/* Language Selector - Desktop (top-right) */}
      <div className="absolute top-4 right-4 hidden sm:block">
        <LanguageSelector />
      </div>
      <div className="mx-auto flex w-full flex-col justify-center space-y-8 sm:w-[400px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">96Beauty</h1>
          <p className="text-muted-foreground">{t("login.subtitle")}</p>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">{t("login.title")}</CardTitle>
            <CardDescription className="text-center">{t("demo.description")}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            {!isDemoMode && (
              <Form {...form}>
                <form onSubmit={onSubmit} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.email")}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={tForms("placeholders.email")}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel>{t("login.password")}</FormLabel>
                          <a
                            href="/forgot-password"
                            className="text-xs text-muted-foreground hover:text-foreground"
                          >
                            {t("login.forgotPassword")}
                          </a>
                        </div>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type={showPassword ? "text" : "password"}
                              placeholder={tForms("placeholders.password")}
                              {...field}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={isLoading} size="lg">
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isLoading ? loadingMessage || t("login.signIn") : t("login.signIn")}
                  </Button>

                  {env.isProdDeploy && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      size="lg"
                      disabled={isLoading}
                      onClick={() => loginWith(DEMO_ACCOUNT)}
                    >
                      {t("login.demoSignIn")}
                    </Button>
                  )}
                </form>
              </Form>
            )}

            {isDemoMode && (
              <div className="space-y-2">
                {demoSections.map((section) => (
                  <div key={section.title} className="space-y-2">
                    {section.users.map((user) => (
                      <Button
                        key={user.email}
                        variant={user.isVip || user.category === "admin" ? "default" : "outline"}
                        size="sm"
                        className="w-full py-3 h-auto justify-start"
                        onClick={() => loginWithDemo(user.email)}
                        disabled={isLoading}
                      >
                        <div className="flex flex-col items-start gap-1">
                          <span className="font-medium">{user.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {t(`roles.${user.role}`)}
                          </span>
                        </div>
                      </Button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Language Selector - Mobile (under card) */}
        <div className="flex justify-center sm:hidden">
          <LanguageSelector />
        </div>
      </div>
    </div>
  );
}
